import type { DayObject } from "@/types/date"
import type { DetectedTripReview } from "@/types/detected-trip"
import type { TripRef } from "@/types/trip"

import { baseApi } from "./base-api"

export interface AcceptDetectedTripArgs {
  detectedTripId: number
  travelEventIds: number[]
  name?: string
  startDate?: DayObject
  endDate?: DayObject
  /** Add the bookings to this trip instead of creating one. */
  tripRef?: TripRef
}

export const detectedTripsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDetectedTripReview: builder.query<DetectedTripReview, void>({
      query: () => ({ url: "/api/detected_trips/review" }),
      providesTags: ["DetectedTripReview"],
    }),

    // Accepting turns a hidden trip into a visible one, or widens an existing
    // trip, so every trips list has to refetch along with the review.
    acceptDetectedTrip: builder.mutation<
      { trip_ref: TripRef },
      AcceptDetectedTripArgs
    >({
      query: ({
        detectedTripId,
        travelEventIds,
        name,
        startDate,
        endDate,
        tripRef,
      }) => ({
        url: `/api/detected_trips/${detectedTripId}/accept`,
        method: "POST",
        data: {
          travel_event_ids: travelEventIds,
          name,
          start_date: startDate,
          end_date: endDate,
          trip_ref: tripRef,
        },
      }),
      invalidatesTags: [
        "DetectedTripReview",
        "Trips",
        "NavTrips",
        "TripsCalendar",
      ],
    }),

    // Adding pending bookings writes itinerary events and can widen the trip's dates.
    acceptFoundBookings: builder.mutation<
      void,
      { tripRef: TripRef; travelEventIds: number[] }
    >({
      query: ({ tripRef, travelEventIds }) => ({
        url: `/api/trips/${tripRef}/found_bookings/accept`,
        method: "POST",
        data: { travel_event_ids: travelEventIds },
      }),
      invalidatesTags: (_result, _error, { tripRef }) => [
        "DetectedTripReview",
        "Trips",
        { type: "Trip", id: tripRef },
        { type: "Itinerary", id: tripRef },
      ],
    }),

    dismissFoundBookings: builder.mutation<void, number[]>({
      query: (travelEventIds) => ({
        url: "/api/detected_trips/dismiss",
        method: "POST",
        data: { travel_event_ids: travelEventIds },
      }),
      invalidatesTags: ["DetectedTripReview"],
    }),
  }),
})

export const {
  useGetDetectedTripReviewQuery,
  useAcceptDetectedTripMutation,
  useAcceptFoundBookingsMutation,
  useDismissFoundBookingsMutation,
} = detectedTripsApi
